import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import AdminNotification from '@app/models-internal/admin-notification';
import OAEntity from '@app/models/oa-entity';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppSettingsService, LogService, UserService } from '@oa/shared-core';

@Injectable()
export class SystemService {
  private readonly _serviceName = 'SystemService';

  /**
   * Subject for the API's status. Is it accessible?
   * 0 = No, 1 = Yes, -1 = Not Set
   */
  public ApiAccessible: BehaviorSubject<number> = new BehaviorSubject<number>(-1);

  public AdminNotifications: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private log: LogService,
    private appSettings: AppSettingsService
  ) {
    this.getApiStatus().subscribe();
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * Gets the API Status
   */
  public getApiStatus() {
    return this.http.get('status', { observe: 'response' }).pipe(
      catchError((err, caught) => {
        if (err.statusText === 'Unknown Error' && err.name === 'HttpErrorResponse') {
          console.log(`${this._serviceName}.getApiStatus result is HttpErrorResponse`);
          this.ApiAccessible.next(0);
        }

        return caught;
      }),

      map((result) => {
        if (this.verboseLogging) {
          console.log(`${this._serviceName}.getApiStatus`, result);
        }

        if (!result) {
          if (this.verboseLogging) {
            console.log(`${this._serviceName}.getApiStatus result is null`);
          }
          this.ApiAccessible.next(0);
        } else {
          if (result.ok) {
            if (this.verboseLogging) {
              console.log(`${this._serviceName}.getApiStatus result is true`);
            }
            this.ApiAccessible.next(1);
          } else {
            console.log(`${this._serviceName}.getApiStatus result is false`);
            if (this.verboseLogging) {
              this.ApiAccessible.next(0);
            }
          }
        }

        return result.ok;
      })
    );
  }

  /**
   * Searches the OA platform for indexed entities with an ID
   * api/system/entity/0
   */
  public searchEntityById(id: number) {
    const url = `system/entity/${id}`;

    return this.http.get<OAEntity[]>(url);
  }

  /**
   * Searches the OA platform for entities with a text string (wildcard)
   * api/system/entity?text=
   */
  public searchEntityByText(text: string) {
    const url = `system/entity?text=${text}`;

    return this.http.get<OAEntity[]>(url);
  }

  public retrieveAdminNotifications() {
    if (!this.userService.isAdmin) {
      return EMPTY;
    }
    const url = 'system/admin/notification';

    // tslint:disable-next-line: no-any
    return this.http.get<AdminNotification[]>(url).pipe(
      map((result) => {
        this.AdminNotifications.next(result);
        return result;
      })
    );
  }
}
